export interface JWTPayload {
  user_id: string
  username: string
  role: string
  exp: number
  iat: number
}

export class JWTManager {
  private static readonly TOKEN_KEY = 'auth_token'

  /**
   * Decode JWT token payload without verification
   * Note: This is for client-side use only. Server should always verify tokens.
   */
  static decodeToken(token: string): JWTPayload | null {
    try {
      const parts = token.split('.')
      if (parts.length !== 3) {
        return null
      }

      const payload = parts[1]
      const decoded = atob(payload.replace(/-/g, '+').replace(/_/g, '/'))
      return JSON.parse(decoded) as JWTPayload
    } catch (error) {
      console.error('Failed to decode JWT token:', error)
      return null
    }
  }

  /**
   * Check if token is expired
   */
  static isTokenExpired(token: string): boolean {
    const payload = this.decodeToken(token)
    if (!payload) {
      return true
    }

    const currentTime = Math.floor(Date.now() / 1000)
    return payload.exp < currentTime
  }

  /**
   * Get token from localStorage
   */
  static getToken(): string | null {
    return localStorage.getItem(this.TOKEN_KEY)
  }

  /**
   * Store token in localStorage
   */
  static setToken(token: string): void {
    localStorage.setItem(this.TOKEN_KEY, token)
  }

  /**
   * Remove token from localStorage
   */
  static removeToken(): void {
    localStorage.removeItem(this.TOKEN_KEY)
  }

  /**
   * Get user info from stored token
   */
  static getUserFromToken(): JWTPayload | null {
    const token = this.getToken()
    if (!token) {
      return null
    }

    if (this.isTokenExpired(token)) {
      this.removeToken()
      return null
    }

    return this.decodeToken(token)
  }

  /**
   * Check if user has specific role
   */
  static hasRole(role: string): boolean {
    const user = this.getUserFromToken()
    return user?.role === role
  }

  /**
   * Check if user has any of the specified roles
   */
  static hasAnyRole(roles: string[]): boolean {
    const user = this.getUserFromToken()
    return user ? roles.includes(user.role) : false
  }

  /**
   * Get time until token expires (in seconds)
   */
  static getTimeUntilExpiry(): number | null {
    const token = this.getToken()
    if (!token) {
      return null
    }

    const payload = this.decodeToken(token)
    if (!payload) {
      return null
    }

    const currentTime = Math.floor(Date.now() / 1000)
    return Math.max(0, payload.exp - currentTime)
  }
}